import { useTheme } from '@material-ui/core';
import React, { FunctionComponent } from 'react';
import DefaultButton from '../../../../../common/components/presentation/Buttons/DefaultButton';
import Translation from '../../../../../common/components/presentation/Translation/Translation';
import { ICheckoutTheme } from '../../../../../common/theme/ICheckoutTheme';
import backToFinanceButtonStyles from './backToFinanceButtonStyles';
import usePlatformConfig from '../../../../../common/react/usePlatformConfig';
import useJourneyStepsContext from '../../../../checkout4/hooks/useJourneyStepsContext';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import useCheckoutContext from '../../../../common/hooks/useCheckoutContext';
import {
  getCheckoutStepsCommonFields,
  getEventDataCommonFields,
  getPurchaseOptionCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../../gql/common/utils/dxpDataLayerCommonFields';
import { DxpDataLayerEventNames } from '../../../../../../../common/utilities/customDatalayerInterface';
import { EventDataTypes } from '../../../../../gql/common/enum/DataLayerEnums';

type BackToFinanceButtonProps = {
  carConfigurationUrl: string;
};

const BackToFinanceButton: FunctionComponent<BackToFinanceButtonProps> = ({ carConfigurationUrl }) => {
  const styles = backToFinanceButtonStyles();

  const {
    checkoutLite: { checkout4 },
  } = useTheme<ICheckoutTheme>();
  const {
    platformConfig: { ga4DxpDataLayer },
  } = usePlatformConfig();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { steps, activeStep } = useJourneyStepsContext();
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();
  const { deal } = useCheckoutContext();

  const navigateToFinanceConfiguration = () => {
    window.location.href = carConfigurationUrl;

    if (deal && steps && activeStep) {
      callDxpDigitalAssignmentEntryEvent(
        {
          ...getEventDataCommonFields(DxpDataLayerEventNames.FINANCE_CTA_CLICK, true),
          event_action: removeHostNameFromUrl(window.location.href),
          event_label: `${EventDataTypes.BUTTON}::${EventDataTypes.BACK_TO_FINANCIAL_CALCULATOR}`,
          event_category: EventDataTypes.CHECKOUT_CLICK,
          event_pagecategory: EventDataTypes.CHECKOUT,
          click_url: removeHostNameFromUrl(window.location.href),
          click_text: EventDataTypes.BACK_TO_FINANCIAL_CALCULATOR,
          click_name: EventDataTypes.BUTTON,
          purchaseOption: {
            price: deal.car.prices?.totalPrice[1]?.money?.amount.toString() ?? '',
            payment_period: deal.finance?.simulation?.loanTermPeriod
              ? `${deal.finance.simulation.loanTermPeriod.value} ${deal.finance.simulation.loanTermPeriod.unit}`
              : '',
            finance_product: deal.finance?.simulation?.financeProductName
              ? deal.finance.simulation.financeProductName.toLowerCase()
              : '',
            down_payment_trade_in_price: '',
            final_deferred_payment: '',
            installment_fee: '',
            total_to_pay: deal.car.prices?.totalPrice[1]?.money?.amount.toString() ?? '',
            monthly_mileage: deal?.finance?.formInput?.mileage?.toString() ?? '',
            vehicle_cash_price: deal?.attributes?.totalPurchaseAmount
              ? deal.attributes.totalPurchaseAmount?.toString().toLowerCase()
              : '',
            ...getPurchaseOptionCommonFields(deal),
          },
          vproduct: {
            availability: deal?.car?.stock ? true : false,
            transmission: deal.car?.engine?.attributes?.transmissionType ?? '',
            max_output: '',
            ...vproductCommonFields(deal),
          },
          checkoutStep: {
            ...getCheckoutStepsCommonFields(steps, activeStep),
          },
        },
        jlrDataLayer,
      );
    }
  };

  return (
    <DefaultButton
      onClick={navigateToFinanceConfiguration}
      className={[styles.buttonWrapper]}
      variant="outlined"
      dataTest={'checkout:finance_section:button'}
      disableTouchRipple={!!checkout4?.disableTouchRipple}
    >
      <Translation id={'trans__checkout__extras_page__finance_back_cta'} className={styles.buttonLabel} />
    </DefaultButton>
  );
};

export default BackToFinanceButton;
